import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  LayoutGridIcon,
  PlugIcon,
  RectangleHorizontalIcon,
  SettingsIcon,
  ShapesIcon,
  ShieldIcon,
  SparklesIcon,
} from 'lucide-react';
import { usePermissions } from '@/entities/user';
import { McpLogo } from '@/shared/ui';
import { ENV } from '@/lib/config/env';
import { ROUTES } from '@/shared/config/routes';
import { useContentStudioEnabled } from '@/shared/config/useContentStudioEnabled';

export interface SettingsRailLink {
  id: string;
  label: string;
  IconComponent: ComponentType<{ className?: string }>;
  /** An in-app route, or an absolute URL when `isExternal`. */
  href: string;
  /** Leaves the app, so it opens in its own tab and never reports itself as the open page. */
  isExternal?: boolean;
}

export interface SettingsRailGroup {
  id: string;
  heading: string;
  items: SettingsRailLink[];
}

/**
 * Whether a rail destination owns the open page.
 *
 * Descendant matching, not the strict equality the tab bar used: Tools has four
 * detail routes beneath it, and a persistent rail that lights nothing tells the
 * reader they are nowhere. Equality was survivable on a tab bar because the tab
 * bar was not on screen at all on those routes.
 */
export const isSectionActive = (pathname: string, route: string): boolean =>
  pathname === route || pathname.startsWith(`${route}/`);

/**
 * The rail's contents, gated exactly as the tab bar it replaces was: same
 * permissions, same flags, same order. Groups that gate down to nothing are
 * dropped whole, so a heading never stands over an empty list.
 */
export const useOrganizationSettingsNav = (): SettingsRailGroup[] => {
  const { t } = useTranslation('admin');
  // The platform's own admin screen already has a name in `common`, on the user
  // popover that links to the same URL. Reused rather than restated so the two
  // entry points can never disagree about what they open.

  const {
    isInstanceAdmin,
    isWorkspaceAdmin,
    canManageModelsWhitelisting,
    canManageTools,
    canManageAdFormats,
  } = usePermissions();
  const mcpCatalogEnabled = useBooleanFlagValue('user_managed_mcp_servers', false);
  const connectedAppsEnabled = useBooleanFlagValue('connected_apps', false);
  const contentStudioEnabled = useContentStudioEnabled();

  const platformItems: SettingsRailLink[] = [
    ...(isWorkspaceAdmin
      ? [
          {
            id: 'admin-settings',
            label: t('admin.organizationSettings.adminSettings'),
            IconComponent: ShieldIcon,
            href: `${ENV.PLATFORM_BASE_URL}/admin`,
            isExternal: true,
          },
        ]
      : []),
    ...(connectedAppsEnabled
      ? [
          {
            id: 'connected-apps',
            label: t('connectedApps.connectedAppsPage.connectedApps'),
            IconComponent: PlugIcon,
            href: ROUTES.ADMIN_CONNECTED_APPS,
          },
        ]
      : []),
  ];

  const toolsItems: SettingsRailLink[] = [
    ...(canManageTools
      ? [
          {
            id: 'tools',
            label: t('admin.adminLayout.tools'),
            IconComponent: ShapesIcon,
            href: ROUTES.ADMIN_TOOLS,
          },
        ]
      : []),
    ...(canManageModelsWhitelisting
      ? [
          {
            id: 'models',
            label: t('admin.adminLayout.aiModels'),
            IconComponent: SparklesIcon,
            href: ROUTES.ADMIN_MODELS,
          },
        ]
      : []),
    ...(isInstanceAdmin
      ? [
          {
            id: 'etl-config',
            label: t('admin.adminLayout.etlConfig'),
            IconComponent: SettingsIcon,
            href: ROUTES.ADMIN_ETL_CONFIG,
          },
        ]
      : []),
    ...(canManageTools && mcpCatalogEnabled
      ? [
          {
            id: 'mcp-catalog',
            label: t('admin.adminLayout.mcpCatalog'),
            IconComponent: McpLogo,
            href: ROUTES.ADMIN_MCP_CATALOG,
          },
        ]
      : []),
    ...(isWorkspaceAdmin
      ? [
          {
            id: 'customization',
            label: t('admin.adminLayout.categoriesCustomization'),
            IconComponent: LayoutGridIcon,
            href: ROUTES.ADMIN_CUSTOMIZATION,
          },
        ]
      : []),
  ];

  // Global data, so it is gated on its own key rather than org or workspace admin - see the
  // page's own notice.
  const contentItems: SettingsRailLink[] =
    canManageAdFormats && contentStudioEnabled
      ? [
          {
            id: 'ad-formats',
            label: t('admin.adminLayout.adFormats'),
            IconComponent: RectangleHorizontalIcon,
            href: ROUTES.ADMIN_AD_FORMATS,
          },
        ]
      : [];

  return [
    { id: 'platform', heading: t('admin.organizationSettings.platform'), items: platformItems },
    { id: 'content', heading: t('admin.organizationSettings.content'), items: contentItems },
    {
      id: 'tools-and-agents',
      heading: t('admin.organizationSettings.toolsAndAgents'),
      items: toolsItems,
    },
  ].filter((group) => group.items.length > 0);
};
